import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  useAiFoundryDebugLog,
  type AiFoundryDebugLogEntry,
} from '../hooks/useAiFoundryDebugLog';
import { useEnsureLywSessionDebugModeSynced } from '../hooks/useSyncLywSessionDebugMode';
import { useSessionDebugMode } from '../hooks/useSessionDebugMode';
type AiActivityContextValue = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  sessionDebugMode: boolean;
  entries: AiFoundryDebugLogEntry[];
  clearEntries: () => void;
};

const AiActivityContext = createContext<AiActivityContextValue | null>(null);

export function AiActivityProvider({ children }: { children: React.ReactNode }) {
  useEnsureLywSessionDebugModeSynced();
  const sessionDebugMode = useSessionDebugMode();
  const [isOpen, setIsOpen] = useState(false);
  const { entries, clearEntries } = useAiFoundryDebugLog(sessionDebugMode);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!sessionDebugMode) {
      setIsOpen(false);
    }
  }, [sessionDebugMode]);

  const value = useMemo(
    () => ({ isOpen, setIsOpen, toggle, sessionDebugMode, entries, clearEntries }),
    [isOpen, sessionDebugMode, entries, clearEntries, toggle]
  );

  return (
    <AiActivityContext.Provider value={value}>
      {children}
    </AiActivityContext.Provider>
  );
}

export function useAiActivity(): AiActivityContextValue {
  const ctx = useContext(AiActivityContext);
  if (!ctx) {
    throw new Error('useAiActivity must be used within AiActivityProvider');
  }
  return ctx;
}
